/**
 * @file BulkQRPage.js
 * @description Public page at /bulk-qr that orchestrates the 3-step bulk
 * QR generation wizard.
 *
 * Architecture:
 *  - A single Paper card acts as the wizard shell; a MUI Stepper above the
 *    content area shows the user where they are in the process.
 *  - All heavy state (headers, rows, validRows) lives here so each step
 *    component receives only what it needs as props — the components are
 *    presentation-only and trivially testable.
 *  - File parsing is kicked off as soon as a file is accepted (after
 *    validation) so the user gets feedback without having to click "Next".
 *  - Column auto-detection fires once the parse completes, pre-populating
 *    the selects when the file uses common header names.
 *  - filterValidRows is called when the user advances from step 1 → 2 rather
 *    than on every select change, to avoid unnecessary re-computations on
 *    large files.
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Stepper,
    Step,
    StepLabel,
    Button,
    Divider,
    Container,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import RefreshIcon from '@material-ui/icons/Refresh';

import FileUploadZone from './components/FileUploadZone';
import ColumnMapper from './components/ColumnMapper';
import GenerateView from './components/GenerateView';
import {
    parseFile,
    filterValidRows,
    autoDetectEVColumn,
    autoDetectChassisColumn,
} from './utils/fileParser';
import useBulkQRGenerator from './hooks/useBulkQRGenerator';

// ─── Constants ────────────────────────────────────────────────────────────────

const STEPS = ['Upload File', 'Map Columns', 'Generate & Download'];

/** Number of valid-row previews passed to GenerateView. */
const GENERATE_PREVIEW_COUNT = 10;

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
    pageRoot: {
        minHeight: '100vh',
        backgroundColor: theme.palette.grey[50],
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(8),
        fontFamily: 'Nunito, sans-serif',
    },
    header: {
        textAlign: 'center',
        marginBottom: theme.spacing(4),
    },
    headerTitle: {
        fontWeight: 800,
        color: '#43419e',
        fontSize: '2rem',
        fontFamily: 'Nunito, sans-serif',
    },
    headerSubtitle: {
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
        fontSize: '1rem',
    },
    wizardPaper: {
        padding: theme.spacing(4),
        borderRadius: theme.shape.borderRadius * 2,
        boxShadow: '0 4px 24px rgba(67, 65, 158, 0.10)',
    },
    stepper: {
        backgroundColor: 'transparent',
        paddingLeft: 0,
        paddingRight: 0,
        marginBottom: theme.spacing(3),
    },
    stepperActive: {
        '& .MuiStepIcon-active': {
            color: '#43419e',
        },
        '& .MuiStepIcon-completed': {
            color: '#388e3c',
        },
    },
    stepContent: {
        minHeight: 260,
    },
    divider: {
        margin: theme.spacing(3, 0),
    },
    navigationRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: theme.spacing(2),
    },
    backButton: {
        color: theme.palette.text.secondary,
        fontWeight: 700,
    },
    nextButton: {
        backgroundColor: '#43419e',
        color: '#fff',
        fontWeight: 700,
        '&:hover': {
            backgroundColor: '#302f80',
        },
        '&.Mui-disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
        },
    },
    startOverButton: {
        color: theme.palette.text.secondary,
        fontWeight: 700,
    },
}));

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * BulkQRPage is the top-level page component for the /bulk-qr route.
 * It manages the wizard state and delegates rendering to step-specific
 * sub-components.
 */
function BulkQRPage() {
    const classes = useStyles();

    // ── Wizard navigation ─────────────────────────────────────────────────────
    const [activeStep, setActiveStep] = useState(0);

    // ── Step 0: file state ────────────────────────────────────────────────────
    const [file, setFile] = useState(null);
    const [isParsing, setIsParsing] = useState(false);
    const [parseError, setParseError] = useState(null);
    const [headers, setHeaders] = useState([]);
    const [rows, setRows] = useState([]);
    const [totalRows, setTotalRows] = useState(0);

    // ── Step 1: column mapping ────────────────────────────────────────────────
    const [evColumn, setEvColumn] = useState('');
    const [chassisColumn, setChassisColumn] = useState('');

    // Running valid/skipped count for the ColumnMapper display — recomputed
    // whenever the column selection changes so the count bar stays live
    const [liveValidCount, setLiveValidCount] = useState(0);
    const [liveSkippedCount, setLiveSkippedCount] = useState(0);

    // ── Step 2: generate state ────────────────────────────────────────────────
    /** Rows that passed the filterValidRows check — computed once on step advance. */
    const [validRows, setValidRows] = useState([]);
    const [skippedCount, setSkippedCount] = useState(0);

    const { generate, cancel, reset: resetGenerator, progress, isRunning } =
        useBulkQRGenerator();

    // ── Derived values ────────────────────────────────────────────────────────

    /** True when step 0 is satisfied (file parsed, no error, has rows). */
    const step0Complete = !isParsing && !parseError && file != null && totalRows > 0;

    /** True when step 1 is satisfied (both columns chosen, different, valid rows exist). */
    const step1Complete =
        !!evColumn &&
        !!chassisColumn &&
        evColumn !== chassisColumn &&
        liveValidCount > 0;

    // ── File handling ─────────────────────────────────────────────────────────

    /**
     * Handle a file being accepted by the FileUploadZone or rejected with a
     * validation error.  Immediately kicks off async parsing on acceptance.
     *
     * @param {File|null} newFile
     * @param {string|null} validationError
     */
    const handleFile = useCallback(async (newFile, validationError) => {
        // Reset all downstream state whenever the file changes
        setFile(newFile);
        setParseError(validationError || null);
        setHeaders([]);
        setRows([]);
        setTotalRows(0);
        setEvColumn('');
        setChassisColumn('');
        setLiveValidCount(0);
        setLiveSkippedCount(0);

        if (!newFile || validationError) return;

        setIsParsing(true);

        try {
            const result = await parseFile(newFile);

            if (result.error) {
                setParseError(result.error);
                setFile(null); // treat as if no file was accepted
            } else {
                setHeaders(result.headers);
                setRows(result.rows);
                setTotalRows(result.totalRows);

                // Attempt to auto-detect the two key columns so common exports
                // work out of the box without manual mapping
                const detectedEV = autoDetectEVColumn(result.headers);
                const detectedChassis = autoDetectChassisColumn(result.headers);
                setEvColumn(detectedEV || '');
                setChassisColumn(detectedChassis || '');
            }
        } catch (err) {
            setParseError(`Unexpected error while parsing: ${err?.message}`);
            setFile(null);
        } finally {
            setIsParsing(false);
        }
    }, []);

    // ── Live valid/skipped count (Step 1) ─────────────────────────────────────

    /**
     * Recompute the valid/skipped counts whenever the column selection changes.
     * This drives the count bar in ColumnMapper without waiting for Next.
     */
    useEffect(() => {
        if (!evColumn || !chassisColumn || evColumn === chassisColumn) {
            setLiveValidCount(0);
            setLiveSkippedCount(rows.length);
            return;
        }

        const { valid, skippedCount: skipped } = filterValidRows(rows, evColumn, chassisColumn);
        setLiveValidCount(valid.length);
        setLiveSkippedCount(skipped);
    }, [rows, evColumn, chassisColumn]);

    // ── Wizard navigation ─────────────────────────────────────────────────────

    const handleNext = useCallback(() => {
        if (activeStep === 1) {
            // Step 1 → 2: materialise the filtered rows so GenerateView has them
            const { valid, skippedCount: skipped } = filterValidRows(
                rows,
                evColumn,
                chassisColumn,
            );
            setValidRows(valid);
            setSkippedCount(skipped);
        }
        setActiveStep((s) => s + 1);
    }, [activeStep, rows, evColumn, chassisColumn]);

    const handleBack = useCallback(() => {
        setActiveStep((s) => Math.max(0, s - 1));
    }, []);

    /** Reset the entire wizard back to step 0. */
    const handleStartOver = useCallback(() => {
        setActiveStep(0);
        setFile(null);
        setIsParsing(false);
        setParseError(null);
        setHeaders([]);
        setRows([]);
        setTotalRows(0);
        setEvColumn('');
        setChassisColumn('');
        setLiveValidCount(0);
        setLiveSkippedCount(0);
        setValidRows([]);
        setSkippedCount(0);
        resetGenerator();
    }, [resetGenerator]);

    // ── Generation ────────────────────────────────────────────────────────────

    const handleGenerate = useCallback(() => {
        const items = validRows.map((row) => ({
            evNumber: row[evColumn],
            chassisNumber: row[chassisColumn],
        }));

        generate(items, {}, { zipBaseName: 'EV_QR_STICKERS' });
    }, [validRows, evColumn, chassisColumn, generate]);

    const handleRetry = useCallback(() => {
        resetGenerator();
    }, [resetGenerator]);

    // ── Next button disabled logic ────────────────────────────────────────────

    const nextDisabled =
        (activeStep === 0 && !step0Complete) ||
        (activeStep === 1 && !step1Complete) ||
        activeStep === 2;

    // Preview rows for GenerateView (first GENERATE_PREVIEW_COUNT valid rows
    // mapped to the shape GenerateView expects)
    const generatePreviewRows = validRows
        .slice(0, GENERATE_PREVIEW_COUNT)
        .map((row) => ({
            evNumber: row[evColumn] || '',
            chassisNumber: row[chassisColumn] || '',
        }));

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <Box className={classes.pageRoot}>
            <Container maxWidth="md">
                {/* Page header */}
                <Box className={classes.header}>
                    <Typography variant="h1" className={classes.headerTitle}>
                        EV Bulk QR Generator
                    </Typography>
                    <Typography variant="body1" className={classes.headerSubtitle}>
                        Upload a CSV or XLSX file to generate print-ready QR stickers for up
                        to 10,000 vehicles at once
                    </Typography>
                </Box>

                {/* Wizard card */}
                <Paper className={classes.wizardPaper} elevation={0}>
                    {/* Stepper */}
                    <Stepper
                        activeStep={activeStep}
                        className={`${classes.stepper} ${classes.stepperActive}`}
                        alternativeLabel
                    >
                        {STEPS.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    <Divider />

                    {/* Step content */}
                    <Box mt={3} className={classes.stepContent}>
                        {/* ── Step 0: Upload ──────────────────────────────── */}
                        {activeStep === 0 && (
                            <FileUploadZone
                                onFile={handleFile}
                                file={file}
                                error={parseError}
                                isParsing={isParsing}
                                totalRows={totalRows}
                            />
                        )}

                        {/* ── Step 1: Map Columns ──────────────────────────── */}
                        {activeStep === 1 && (
                            <ColumnMapper
                                headers={headers}
                                rows={rows}
                                evColumn={evColumn}
                                chassisColumn={chassisColumn}
                                onEvColumnChange={setEvColumn}
                                onChassisColumn={setChassisColumn}
                                validCount={liveValidCount}
                                skippedCount={liveSkippedCount}
                                totalRows={totalRows}
                            />
                        )}

                        {/* ── Step 2: Generate & Download ──────────────────── */}
                        {activeStep === 2 && (
                            <GenerateView
                                totalRows={totalRows}
                                validCount={validRows.length}
                                skippedCount={skippedCount}
                                previewRows={generatePreviewRows}
                                isRunning={isRunning}
                                progress={progress}
                                onGenerate={handleGenerate}
                                onCancel={cancel}
                                onRetry={handleRetry}
                            />
                        )}
                    </Box>

                    <Divider className={classes.divider} />

                    {/* Navigation row */}
                    <Box className={classes.navigationRow}>
                        {/* Left side: Back or Start Over */}
                        {activeStep === 0 ? (
                            <Box /> // spacer so the Next button stays on the right
                        ) : progress.stage === 'done' ? (
                            <Button
                                variant="text"
                                className={classes.startOverButton}
                                startIcon={<RefreshIcon />}
                                onClick={handleStartOver}
                            >
                                Start Over
                            </Button>
                        ) : (
                            <Button
                                variant="text"
                                className={classes.backButton}
                                startIcon={<ArrowBackIcon />}
                                onClick={handleBack}
                                disabled={isRunning}
                            >
                                Back
                            </Button>
                        )}

                        {/* Right side: Next (hidden on final step) */}
                        {activeStep < 2 && (
                            <Button
                                variant="contained"
                                className={classes.nextButton}
                                endIcon={<ArrowForwardIcon />}
                                onClick={handleNext}
                                disabled={nextDisabled}
                            >
                                Next
                            </Button>
                        )}

                        {/* On the final step, show Start Over when done, nothing otherwise */}
                        {activeStep === 2 && progress.stage === 'done' && (
                            <Button
                                variant="contained"
                                className={classes.nextButton}
                                startIcon={<RefreshIcon />}
                                onClick={handleStartOver}
                            >
                                Generate Another Batch
                            </Button>
                        )}
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
}

export default BulkQRPage;
